import {ApolloProvider, InMemoryCache, ApolloClient, ApolloLink, Observable, createHttpLink, gql} from "@apollo/client";
import React, {FC, ReactNode} from "react";
import {Modal} from "antd";
import fetch from "unfetch";
import LocalResolvers from "../common/LocalResolver";
import env from "../../../shared/const";
import {ORGANIZATION_ID, SUBDOMAIN} from "../../const";

type AssistantApolloWrapperProps = {
    children: ReactNode;
};

const ASSISTANT_APP_APPLICATION_TYPE = "assistant-app";

// Dynamische API-URL basierend auf Umgebung
let uri = "/graphql"; // Default Development - Webpack Proxy leitet weiter

if (SUBDOMAIN === "stage") {
    uri = "https://api.stage.kursorganizer.com/graphql";
} else if (env.IS_PRODUCTION) {
    uri = "https://api.kursorganizer.com/graphql";
} else if (env.IS_STAGING) {
    uri = "https://api.stage.kursorganizer.com/graphql";
}

const cache = new InMemoryCache({
    addTypename: false,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    dataIdFromObject: () => false,
});

const LOCAL_STATE_INIT = gql`
    query LocalStateInit {
        LocalState @client {
            createCourse {
                locationId
            }
            bookAttendees {
                selectedAttendee {
                    id
                    firstname
                    bookedCourseIds
                }
                selectedCourseId
            }
        }
    }
`;

cache.writeQuery({
    query: LOCAL_STATE_INIT,
    data: {
        LocalState: {
            createCourse: {
                locationId: null,
            },
            bookAttendees: {
                selectedAttendee: {
                    id: null,
                    firstname: null,
                    bookedCourseIds: null,
                },
                selectedCourseId: null,
            },
        },
    },
});

const MAX_RETRIES = 3;
const MODAL_DEBOUNCE_TIME = 5000; // 5 Sekunden zwischen Modals

const networkErrorModalState = {
    lastShownTimestamp: 0,
    isCurrentlyShowing: false,
    resetTimeout: null as NodeJS.Timeout | null,
};

const shouldShowNetworkErrorModal = (): boolean => {
    const now = Date.now();

    if (networkErrorModalState.isCurrentlyShowing) {
        return false;
    }

    const timeSinceLastModal = now - networkErrorModalState.lastShownTimestamp;

    if (timeSinceLastModal < MODAL_DEBOUNCE_TIME) {
        return false;
    }

    networkErrorModalState.isCurrentlyShowing = true;
    networkErrorModalState.lastShownTimestamp = now;

    // Auto-Reset nach 10 Sekunden falls Callbacks nicht feuern
    if (networkErrorModalState.resetTimeout) {
        clearTimeout(networkErrorModalState.resetTimeout);
    }
    networkErrorModalState.resetTimeout = setTimeout(() => {
        networkErrorModalState.isCurrentlyShowing = false;
        networkErrorModalState.resetTimeout = null;
    }, 10000);

    return true;
};

const resetNetworkErrorModal = () => {
    networkErrorModalState.isCurrentlyShowing = false;
    if (networkErrorModalState.resetTimeout) {
        clearTimeout(networkErrorModalState.resetTimeout);
        networkErrorModalState.resetTimeout = null;
    }
};

const errorLink = new ApolloLink((operation, forward) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const attemptRequest = (retryCount: number): Observable<any> => {
        return new Observable((observer) => {
            let retryTimeoutId: NodeJS.Timeout | null = null;
            let retrySubscription: any = null;
            let isUnsubscribed = false;

            const subscription = forward(operation).subscribe({
                next: (result) => {
                    if (!isUnsubscribed) {
                        observer.next(result);
                    }
                },
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                error: (error: any) => {
                    if (isUnsubscribed) {
                        return;
                    }

                    const isNetworkError =
                        error?.networkError || error instanceof ProgressEvent || error?.message?.includes("Network error") || error?.message?.includes("Failed to fetch");

                    if (isNetworkError && retryCount < MAX_RETRIES) {

                        const delay = Math.min(500 * 2 ** retryCount, 2000);

                        retryTimeoutId = setTimeout(() => {
                            if (isUnsubscribed) {
                                return;
                            }

                            retrySubscription = attemptRequest(retryCount + 1).subscribe({
                                next: (result) => {
                                    if (!isUnsubscribed) {
                                        observer.next(result);
                                    }
                                },
                                error: (err) => {
                                    if (!isUnsubscribed) {
                                        observer.error(err);
                                    }
                                },
                                complete: () => {
                                    if (!isUnsubscribed) {
                                        observer.complete();
                                    }
                                },
                            });
                        }, delay);

                        return;
                    }

                    if (isNetworkError) {
                        if (shouldShowNetworkErrorModal()) {
                            Modal.error({
                                title: "Verbindungsfehler",
                                content: "Es besteht keine Verbindung zum Server. Bitte überprüfe deine Internetverbindung oder versuche es später erneut.",
                                okText: "Neu laden",
                                onOk: () => {
                                    resetNetworkErrorModal();
                                    window.location.reload();
                                },
                                onCancel: () => {
                                    resetNetworkErrorModal();
                                },
                                afterClose: () => {
                                    resetNetworkErrorModal();
                                },
                            });
                        }

                    }

                    if (!isUnsubscribed) {
                        observer.error(error);
                    }
                },
                complete: () => {
                    if (!isUnsubscribed) {
                        observer.complete();
                    }
                },
            });

            return () => {
                isUnsubscribed = true;

                if (retryTimeoutId) {
                    clearTimeout(retryTimeoutId);
                    retryTimeoutId = null;
                }

                subscription.unsubscribe();

                if (retrySubscription) {
                    retrySubscription.unsubscribe();
                    retrySubscription = null;
                }
            };
        });
    };

    return attemptRequest(0);
});

const httpLink = createHttpLink({
    fetch: fetch as any,
    uri,
    headers: {
        "x-ko-organization-id": ORGANIZATION_ID,
        "x-application-type": ASSISTANT_APP_APPLICATION_TYPE,
    },
    credentials: "include",
});

export const assistantApolloClient = new ApolloClient({
    link: ApolloLink.from([errorLink, httpLink]),
    cache,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolvers: LocalResolvers as any,
});

export const AssistantURIContext = React.createContext("");

const AssistantApolloWrapper: FC<AssistantApolloWrapperProps> = ({children}) => {
    return (
        <AssistantURIContext.Provider value={uri}>
            <ApolloProvider client={assistantApolloClient}>
                {children}
            </ApolloProvider>
        </AssistantURIContext.Provider>
    );
};

export default AssistantApolloWrapper;

